import { createApiClient } from './client'

// Types
export interface GearNeed {
  id: string
  trip_id: string
  name: string
  description?: string
  category: string
  quantity_needed: number
  is_essential: boolean
  added_by_user_id: string
  notes?: string
  created_at: string
  updated_at: string
  claims?: GearClaim[]
  added_by_user: {
    id: string
    name: string
    avatar_url?: string
    profile_image_path?: string | null
  }
}

export interface GearClaim {
  id: string
  gear_need_id: string
  member_id: string
  commitment_level: 'confirmed' | 'cant_bring'
  quantity_claimed: number
  notes?: string
  claimed_at: string
  member: {
    id: string
    user_id: string
    military_role?: string
    user: {
      id: string
      name: string
      avatar_url?: string
      profile_image_path?: string | null
    }
  }
}

export interface CreateGearNeedRequest {
  name: string
  description?: string
  category?: string
  quantity_needed?: number
  is_essential?: boolean
  notes?: string
}

export interface UpdateGearNeedRequest {
  name?: string
  description?: string
  category?: string
  quantity_needed?: number
  is_essential?: boolean
  notes?: string
}

export interface CreateGearClaimRequest {
  commitment_level: 'confirmed' | 'cant_bring'
  quantity_claimed?: number
  notes?: string
}

export interface UpdateGearClaimRequest {
  commitment_level?: 'confirmed' | 'cant_bring'
  quantity_claimed?: number
  notes?: string
}

export function getGearQuantities(gearNeed: GearNeed, currentUserId?: string): {
  totalConfirmedQuantity: number
  userConfirmedQuantity: number
  remainingQuantity: number
} {
  const claims = gearNeed.claims ?? []
  const confirmedClaims = claims.filter((c) => c.commitment_level === 'confirmed')

  const totalConfirmedQuantity = confirmedClaims.reduce(
    (sum, claim) => sum + (claim.quantity_claimed ?? 1),
    0
  )

  const userConfirmedQuantity =
    currentUserId != null
      ? confirmedClaims
          .filter((claim) => claim.member?.user_id === currentUserId)
          .reduce((sum, claim) => sum + (claim.quantity_claimed ?? 1), 0)
      : 0

  const remainingQuantity = Math.max(
    gearNeed.quantity_needed - (totalConfirmedQuantity - userConfirmedQuantity),
    0
  )

  return {
    totalConfirmedQuantity,
    userConfirmedQuantity,
    remainingQuantity
  }
}

export class GearNeedsApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Get all gear needs for a trip
  async getTripGearNeeds(tripId: string): Promise<GearNeed[]> {
    const response = await this.apiClient.get(`/trips/${tripId}/gear-needs`)
    return (response as any).data
  }

  // Get single gear need
  async getGearNeedById(needId: string): Promise<GearNeed> {
    const response = await this.apiClient.get(`/gear-needs/${needId}`)
    return (response as any).data
  }

  // Create new gear need
  async createGearNeed(tripId: string, gearNeedData: CreateGearNeedRequest): Promise<GearNeed> {
    const response = await this.apiClient.post(`/trips/${tripId}/gear-needs`, gearNeedData)
    return (response as any).data
  }

  // Update gear need
  async updateGearNeed(needId: string, updateData: UpdateGearNeedRequest): Promise<GearNeed> {
    const response = await this.apiClient.patch(`/gear-needs/${needId}`, updateData)
    return (response as any).data
  }

  // Delete gear need
  async deleteGearNeed(needId: string): Promise<void> {
    await this.apiClient.delete(`/gear-needs/${needId}`)
  }

  // Claim gear need
  async claimGearNeed(needId: string, claimData: CreateGearClaimRequest): Promise<GearClaim> {
    const response = await this.apiClient.post(`/gear-needs/${needId}/claim`, claimData)
    return (response as any).data
  }

  // Update gear claim
  async updateGearClaim(needId: string, updateData: UpdateGearClaimRequest): Promise<GearClaim> {
    const response = await this.apiClient.patch(`/gear-needs/${needId}/claim`, updateData)
    return (response as any).data
  }

  // Unclaim gear need
  async unclaimGearNeed(needId: string): Promise<void> {
    await this.apiClient.delete(`/gear-needs/${needId}/claim`)
  }
}
